import React, {PureComponent} from 'react';
import {
	Card,
	CardContent,
	Typography,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardActionArea from "@mui/material/CardActionArea";
import Collapse from "@mui/material/Collapse";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Grid";
import WorkoutSmallNote from "../../workout/partials/WorkoutSmallNote";
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {MicroInsightUtils} from "../../../core/insights/MicroInsightUtils";
import AchievementBadgesStrip from "./AchievementBadgesStrip";

const useStyles = theme => ({});

class WorkoutStreakCard extends PureComponent {

	constructor(props) {
		super(props);

		this.state = {
			streak: UserLocalStorage.get(UrlBuilder.user.streakApi()) ?? null,
			isCollapseOpen: false,
			isLoading: false,
			hasLoadedAtLeastOnce: false,
			isSavingGoal: false,
		};
	}

	componentDidMount() {
		this.fetch();
	}

	hasNeverBeenFetched = () => {
		return this.state.streak === null;
	}

	fetch = async () => {
		this.setState({ isLoading: true });

		const response = await fetch(UrlBuilder.user.streakApi());

		if (response.status !== 200) {
			this.setState({ isLoading: false });
			return;
		}

		const responseData = await response.json();

		this.setState({
			streak: responseData['data'] ?? null,
			isLoading: false,
			hasLoadedAtLeastOnce: true,
		});

		UserLocalStorage.set(UrlBuilder.user.streakApi(), responseData['data'] ?? null);
	}

	triggerCollapse = () => {
		this.setState({
			isCollapseOpen: !this.state.isCollapseOpen,
		});
	}

	saveStreak = async (updates) => {
		this.setState({ isSavingGoal: true });

		const body = {
			weeklyWorkoutGoal: this.state.streak.weeklyWorkoutGoal,
			hasStreakNotifications: this.state.streak.hasStreakNotifications,
			...updates,
		};

		const response = await fetch(UrlBuilder.user.streakApi(), {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});

		if (response.status === 200) {
			const responseData = await response.json();
			this.setState({
				streak: responseData['data'],
				isSavingGoal: false,
			});
			UserLocalStorage.set(UrlBuilder.user.streakApi(), responseData['data']);
		} else {
			this.setState({ isSavingGoal: false });
		}
	}

	updateGoal = (newGoal) => {
		this.saveStreak({ weeklyWorkoutGoal: newGoal });
	}

	toggleNotifications = () => {
		this.saveStreak({ hasStreakNotifications: !this.state.streak.hasStreakNotifications });
	}

	renderGoalSelector = (currentGoal) => {
		const goals = [1, 2, 3, 4, 5, 6, 7];

		return <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
			{goals.map(g =>
				<Chip
					key={g}
					label={`${g}x`}
					color={g === currentGoal ? "primary" : "default"}
					variant={g === currentGoal ? "filled" : "outlined"}
					onClick={() => this.updateGoal(g)}
					disabled={this.state.isSavingGoal}
					size="medium"
				/>
			)}
		</Box>;
	}

	renderProgressBar = (workoutsThisWeek, goal) => {
		const progress = Math.min((workoutsThisWeek / goal) * 100, 100);
		const completed = workoutsThisWeek >= goal;

		return <Box sx={{ width: '100%' }}>
			<LinearProgress
				variant="determinate"
				value={progress}
				color={completed ? "success" : "primary"}
				sx={{ height: 8, borderRadius: 4 }}
			/>
		</Box>;
	}

	renderCardContent = (streak) => {
		return <Grid container spacing={1}>
			<Grid item xs={12}>
				{this.renderProgressBar(streak.workoutsThisWeek, streak.weeklyWorkoutGoal)}
				<Box paddingTop={1}/>
			</Grid>
			<Grid item xs={6}>
				<WorkoutSmallNote title="Current Streak" description={`${streak.currentWeekStreak} wk`} smallDetail="Consecutive weeks"/>
			</Grid>
			<Grid item xs={6}>
				<WorkoutSmallNote title="Longest Streak" description={`${streak.longestWeekStreak} wk`} smallDetail="All time best"/>
			</Grid>
			<Grid item xs={12}>
				<Box paddingTop={1}/>
				<Divider variant='middle'/>
				<Box paddingTop={1.5}/>
				<Typography variant="body2" color="textSecondary" align="center" gutterBottom>
					Weekly Goal
				</Typography>
				{this.renderGoalSelector(streak.weeklyWorkoutGoal)}
			</Grid>
			<Grid item xs={12}>
				<Box paddingTop={0.5}/>
				<Divider variant='middle'/>
				<Box display="flex" justifyContent="center" paddingTop={1}>
					<FormControlLabel
						control={
							<Switch
								checked={streak.hasStreakNotifications}
								onChange={this.toggleNotifications}
								disabled={this.state.isSavingGoal}
								size="small"
								color="secondary"
							/>
						}
						label={<Typography variant="body2" color="textSecondary">Streak notifications</Typography>}
					/>
				</Box>
			</Grid>
		</Grid>;
	}

	render() {
		const {streak} = this.state;

		const title = streak && streak.currentWeekStreak > 0
			? `${streak.currentWeekStreak} Week Streak`
			: 'Workout Streak';

		const subtitle = streak
			? `${streak.workoutsThisWeek} / ${streak.weeklyWorkoutGoal} this week`
			: null;

		const insightText = streak ? MicroInsightUtils.getStreakInsight(streak) : null;

		return (
			<Card variant='outlined'>
				<CardActionArea>
					<CardHeader
						avatar={<WhatshotIcon sx={{ color: streak && streak.currentWeekStreak > 0 ? '#ff6d00' : 'inherit' }}/>}
						title={<Typography variant='h6' sx={{ fontSize: 17 }}>{title}</Typography>}
						subheader={<>
							{subtitle}
							{insightText && (
								<Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'secondary.main' }}>
									<AutoAwesomeIcon sx={{ fontSize: 14 }}/> {insightText}
								</Typography>
							)}
						</>}
						style={{paddingBottom: '10px', paddingTop: '10px'}}
						action={
							<IconButton size="small">
								{this.state.isCollapseOpen ? <ExpandLess/> : <ExpandMore/>}
							</IconButton>
						}
						onClick={this.triggerCollapse}
					/>
					<Collapse in={this.state.isLoading}>
						<LinearProgress/>
					</Collapse>
				</CardActionArea>

				<Collapse in={this.state.isCollapseOpen}>
					<Divider/>
					<CardContent>
						{this.hasNeverBeenFetched() ?
							<Skeleton animation="pulse" variant="rectangular" style={{height: 150}}/>
							: this.renderCardContent(streak)
						}
					</CardContent>
				</Collapse>

				<Box px={2} pb={0.5}>
					<AchievementBadgesStrip/>
				</Box>
			</Card>
		);
	}
}

export default withStyles(useStyles)(WorkoutStreakCard);
